"use client";

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Heart, Coffee, Gift, Star, ArrowLeft, Sparkles, Copy, Check } from 'lucide-react';
import Link from 'next/link';
import { toast } from 'sonner';
import TossIcon from './toss';

export default function DonatePage() {
  const [isHovered, setIsHovered] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const accountInfo = {
    bank: '케이뱅크',
    accountNo: '1001 4102 0176',
    holder: '카미봇'
  };

  const handleDonate = () => {
    window.location.href = 'supertoss://send?amount=3000&bank=%EC%BC%80%EC%9D%B4%EB%B1%85%ED%81%AC&accountNo=100141020176&origin=qr';
  };

  const handleCopyAccount = async () => {
    try {
      await navigator.clipboard.writeText(`${accountInfo.bank} ${accountInfo.accountNo}`);
      setIsCopied(true);
      toast.success('계좌번호가 복사되었어요! 📋', {
        description: `${accountInfo.bank} ${accountInfo.accountNo}`,
      });

      // 2초 후 아이콘 원래대로 변경
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (err) {
      toast.error('복사에 실패했어요 😅', {
        description: '직접 입력해주세요: 케이뱅크 100141020176',
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-indigo-50">
      {/* 헤더 */}
      <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
        <div className="container mx-auto py-4 px-6 flex items-center justify-between">
          <Link href="/" className="flex items-center space-x-2 text-pink-700 hover:text-pink-500 transition-colors">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">홈으로 돌아가기</span>
          </Link>

          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-full bg-pink-500 flex items-center justify-center">
              <Heart className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold text-xl text-pink-700">카미에게 기부하기</span>
          </div>
        </div>
      </header>

      {/* 메인 콘텐츠 */}
      <main className="container mx-auto max-w-4xl px-6 py-12">
        {/* 히어로 섹션 */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-pink-100 text-pink-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Heart className="h-4 w-4" />
            <span>감사의 마음을 전해요</span>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
            카미봇에게 힘을 주세요! 💪
          </h1>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            저는 카미봇을 만들고 유지 보수하는 카미(Kamilake)라고 해요.<br />
            여러분의 작은 기부가 더 나은 카미봇을 만드는 큰 힘이 된답니다.
          </p>
        </div>

        {/* 후원 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {/* 메인 후원 카드 */}
          <Card className="relative overflow-hidden bg-gradient-to-br from-pink-500 to-purple-600 border-none text-white shadow-2xl">
            <div className="absolute inset-0 bg-gradient-to-br from-pink-400/20 to-purple-500/20"></div>
            <CardHeader className="relative z-10">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <Coffee className="h-6 w-6" />
                </div>
                <div>
                  <CardTitle className="text-2xl font-bold">라면 하나 사주기!</CardTitle>
                  <CardDescription className="text-pink-100">
                    3,000원으로 개발자의 아침 삼각김밥을 사주세요..!
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="relative z-10">
              <div className="space-y-4 mb-6">
                <div className="flex items-center space-x-3 text-pink-100">
                  <Star className="h-5 w-5" />
                  <span>개발 속도가 빨라져요!</span>
                </div>
                <div className="flex items-center space-x-3 text-pink-100">
                  <Heart className="h-5 w-5" />
                  <span>개발자의 행복도가 올라가요! (제일 중요)</span>
                </div>
                <div className="flex items-center space-x-3 text-pink-100">
                  <Gift className="h-5 w-5" />
                  <span>새로운 기능이 더 빨리 나와요!</span>
                </div>
              </div>

              <Button
                onClick={handleDonate}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className="w-full bg-white text-pink-600 hover:bg-pink-50 font-bold py-4 text-lg shadow-lg transform transition-all hover:shadow-xl mb-3 hover:scale-107"
              >
                <Coffee className={`mr-1 h-5 w-5 transition-transform ${isHovered ? 'animate-bounce' : ''}`} />
                라면 하나 사주기! <TossIcon className={`ml-1 h-5 w-5 transition-transform ${isHovered ? 'animate-bounce' : ''}`} />
              </Button>

              <Button
                onClick={handleCopyAccount}
                variant="outline"
                className="w-full bg-white/20 text-white border-white/30 hover:bg-white/30 font-medium py-3 text-sm shadow-md transition-all"
              >
                {isCopied ? (
                  <>
                    <Check className="mr-2 h-4 w-4 text-green-400" />
                    복사 완료! ✨
                  </>
                ) : (
                  <>
                    {accountInfo.bank} {accountInfo.accountNo}
                    <Copy className="mx-1 h-4 w-4" />복사
                  </>
                )}
              </Button>

              <p className="text-center text-pink-100 text-sm mt-3 mb-0">
                토스로 간편하게 기부하거나 직접 송금할 수도 있어요
              </p>
            </CardContent>
          </Card>

          {/* 감사 메시지 카드 */}
          <Card className="bg-white/80 backdrop-blur-sm border-pink-200 shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl font-bold text-gray-800 flex items-center">
                <Heart className="mr-2 h-6 w-6 text-pink-500" />
                감사의 마음을 전해요
              </CardTitle>
            </CardHeader>

            <CardContent>
              <div className="space-y-4 text-gray-600">
                <p className="leading-relaxed">
                  안녕하세요! 카미봇을 사랑해주시는 모든 분들께 진심으로 감사드려요! 🙏
                </p>

                <p className="leading-relaxed">
                  카미봇은 개인 개발자가 혼자서 만들고 있는 프로젝트예요.
                  서버 비용부터 개발과 유지 보수까지, 모든 것을 저 혼자 부담하고 있답니다.
                </p>

                <p className="leading-relaxed">
                  기부를 못하신다면 안 하셔도 되지만, 카미봇을 지원해 주신다면 정말 큰 힘이 돼요.
                </p>

                <p className="leading-relaxed">
                  항상 관심 가져주셔서 정말 고마워요
                </p>

                {/* <div className="bg-pink-50 p-4 rounded-lg border-l-4 border-pink-400">
                  <p className="text-sm text-pink-700 font-medium">
                    💡 후원해주신 분들께는 특별한 뱃지와 함께 감사 인사를 전해드려요!
                  </p>
                </div> */}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 구분선 */}
        <Separator className="my-12 bg-pink-200" />

        {/* FAQ 섹션 */}
        <div className="space-y-8">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">
            궁금한 것들이 있으시다면? 🤔
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-white/60 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-800">
                  💰 기부금은 어떻게 사용되나요?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  서버 운영비, 개발 도구 구입, 그리고 개발자의 야식비(?)로 사용될 예정이에요.
                  서버 API 요금을 내는 것만으로도 마이너스라서, 아마 여기에 다 쓸 것 같아요!
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/60 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-800">
                  🎁 기부하면 특별한 혜택이 있나요?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  기부한다구 특별한 기능이 생기는 건 아니지만, 원하는 기능을 우선적으로 만들어드리도록 노력해 볼게요. 버그 수정이나 고객 지원 역시 우선적으로 해 드릴 수 있어요!
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/60 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-800">
                  🔒 기부 버튼은 안전한가요?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  네! 이건 그냥 토스 계좌이체 버튼이에요.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/60 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-800">
                  💌 개발자에게 메시지를 보낼 수 있을까요?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Discord 서버나 DM을 통해 언제든지 연락주세요.
                  여러분의 의견과 응원을 정말 소중히 생각해요!
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/60 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-800">
                  🏦 토스가 없어도 기부할 수 있나요?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-gray-600">
                  <p>물론이에요! 계좌번호 복사 버튼을 눌러서 직접 송금하실 수 있어요:</p>
                  <div className="bg-gray-100 p-3 rounded-lg font-mono text-sm">
                    <div className="flex items-center justify-between">
                      <span>{accountInfo.bank} {accountInfo.accountNo}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleCopyAccount}
                        className="h-8 px-2"
                      >
                        {isCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* <Card className="bg-white/60 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-gray-800">
                  📱 모바일에서도 쉽게 후원할 수 있나요?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  네! 토스 앱이 설치되어 있다면 버튼 하나로 바로 후원할 수 있어요!
                  토스가 없으시면 계좌번호를 복사해서 은행 앱으로 송금해주세요! 📲
                </p>
              </CardContent>
            </Card> */}
          </div>
        </div>

        {/* 마지막 감사 메시지 */}
        <div className="text-center mt-16 p-8 bg-gradient-to-r from-pink-100 to-purple-100 rounded-2xl">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">
            카미봇과 함께해주셔서 정말 고마워요! 🥰
          </h3>
          <p className="text-gray-600 max-w-2xl mx-auto">
            여러분의 관심과 사랑이 카미봇을 더욱 특별하게 만들어주고 있어요.
            앞으로도 더 안정적이고, 더 멋진 기능들을 열심히 만들어 볼게요!
          </p>
        </div>
      </main>
    </div>
  );
}
